import {
    Box,
    Button,
    Divider,
    Grid,
    Icon,
    Pagination,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import * as React from "react";
import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ProjectSearchType } from "../../../../api/psc";
import {
    EmptyListMessage,
    EndOfInfiniteListMessage,
    PageWithFilter,
} from "../../../../components/exports";
import FilteredPage from "../../../../components/shared/FilteredPage";
import { defaultLimit } from "../../../../constants/GlobalConstants";
import { DialogContext } from "../../../../contexts/DialogContext";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import ProjectDetailDialog from "../../../project-detail/ProjectDetailDialog";
import { useTutorialContext } from "../../../tutorialModule/TutorialContext";
import TutorialWrapper from "../../../tutorialModule/TutorialWrapper";
import DesktopProjectToolbox from "../../components/DesktopProjectToolbox";
import MobileProjectToolbox from "../../components/MobileProjectToolbox";
import ProjectItem from "../../components/ProjectItem";
import ProjectsFilter from "../../components/ProjectsFilter";
import GoToPageDialog from "../../dialogs/GoToPageDialog";
import {
    FetchProjects,
    ProjectState,
    setProjectsFilter,
    setProjectsPage,
} from "../../slice/ProjectSlice";

type TProps = {
    isSmallOrDownScreen: boolean;
    fetch: () => void;
    onProjectTypeChange: (type: ProjectSearchType) => void;
    projectState: ProjectState;
    handlePageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
    openGoToPage: () => void;
    handleRedirectToMap: () => void;
};

export default function ProjectsView() {
    const dispatch = useDispatch();
    const projectState = useAppSelector((state) => state.projects);

    const navigate = useNavigate();

    const { openDialog } = useContext(DialogContext);
    const { activeStep } = useTutorialContext();

    const theme = useTheme();
    const isSmallOrDownScreen = useMediaQuery(theme.breakpoints.down("md"));

    let { id } = useParams();

    useEffect(() => {
        fetch();
    }, [projectState.id]);

    useEffect(() => {
        if (id) {
            openDialog(<ProjectDetailDialog projectHashId={id} />, () =>
                navigate(`#`)
            );
        }
    }, [id]);

    const fetch = async () => {
        FetchProjects(projectState, dispatch);
    };

    const handlePageChange = async (
        event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        dispatch(setProjectsPage(page - 1));
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
    };

    const onProjectTypeChange = (type: ProjectSearchType) => {
        dispatch(setProjectsFilter({ ...projectState.filter, type: type }));
    };

    const openGoToPage = () => {
        openDialog(
            <GoToPageDialog
                current={projectState.page + 1}
                max={Math.ceil(projectState.totalCount / defaultLimit)}
                onChange={(page) => {
                    dispatch(setProjectsPage(page - 1));
                    window.scroll({ top: 0, left: 0, behavior: "smooth" });
                }}
            />
        );
    };

    const handleOpenMobileProject = () => {
        if (activeStep === "mobile-signup-7") {
            openDialog(<ProjectsFilter />);
        }
    };

    return (
        <Box sx={{ overflow: "auto", height: "100%" }}>
            <PageWithFilter filter={<ProjectsFilter />}>
                <FilteredPage>
                    {isSmallOrDownScreen ? (
                        <MobileProjectToolbox
                            fetchProjects={fetch}
                            setProjectType={onProjectTypeChange}
                            isRequirements={false}
                            filterElement={<ProjectsFilter />}
                            state={projectState}
                        />
                    ) : (
                        <DesktopProjectToolbox
                            state={projectState}
                            isRequirements={false}
                            fetchProjects={fetch}
                            setProjectType={onProjectTypeChange}
                        />
                    )}

                    {!projectState.isLoading &&
                        projectState.projects.length === 0 && (
                            <EmptyListMessage message="پروژه ای یافت نشد" />
                        )}

                    {projectState.projects.length > 0 && (
                        <>
                            <Grid container spacing={2}>
                                {projectState.projects.map((p, i) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={6}
                                            lg={4}
                                            xl={3}
                                            key={p.hashId}
                                        >
                                            {i === 0 ? (
                                                <TutorialWrapper
                                                    steps={[
                                                        "desktop-signup-6",
                                                        "mobile-signup-7",
                                                    ]}
                                                    onPreviousStep={
                                                        handleOpenMobileProject
                                                    }
                                                    position={
                                                        isSmallOrDownScreen
                                                            ? "top"
                                                            : "left"
                                                    }
                                                    renderChildren={(
                                                        wa,
                                                        control: any
                                                    ) => (
                                                        <Box
                                                            {...control}
                                                            onClick={() => wa()}
                                                        >
                                                            <ProjectItem
                                                                index={i}
                                                                isLoading={
                                                                    projectState.isLoading
                                                                }
                                                                project={p}
                                                            />
                                                        </Box>
                                                    )}
                                                />
                                            ) : (
                                                <ProjectItem
                                                    index={i}
                                                    isLoading={
                                                        projectState.isLoading
                                                    }
                                                    project={p}
                                                />
                                            )}
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Divider sx={{ mt: 2, mb: 2 }} />

                            {projectState.totalCount > defaultLimit ? (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Pagination
                                        count={Math.ceil(
                                            projectState.totalCount /
                                                defaultLimit
                                        )}
                                        color="primary"
                                        page={projectState.page + 1}
                                        onChange={handlePageChange}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={openGoToPage}
                                        startIcon={<Icon>pageview</Icon>}
                                        size="small"
                                    >
                                        برو به صفحه
                                    </Button>
                                </Stack>
                            ) : (
                                <EndOfInfiniteListMessage />
                            )}
                        </>
                    )}
                </FilteredPage>
            </PageWithFilter>
        </Box>
    );
}
